import type {
  PodcastMediaKit,
  YoutubeAudienceAnalytics,
} from "../api/podcasts";
import type { StatsPayload } from "./metricsExportTypes";
import { buildDemographicsTable } from "./programStats/metricsDetail/metricsTables";

type AudienceSlice = {
  gender?: Array<{ type?: string }>;
  ageRanges?: Array<{ range?: string }>;
} | null | undefined;

function audienceHasGenderOrAge(aud: AudienceSlice): boolean {
  return (aud?.gender?.length ?? 0) > 0 || (aud?.ageRanges?.length ?? 0) > 0;
}

/** OAuth gender/age present on any platform (follower-only stats do not count). */
export function hasRealAudienceDemographics(
  analytics:
    | PodcastMediaKit["analytics"]
    | StatsPayload["analytics"]
    | undefined,
): boolean {
  const yt = analytics?.youtube;
  const ytAudience = yt?.longForm?.audience ?? yt?.audience;
  const igAudience = analytics?.instagram?.audience;
  const ttAudience = analytics?.tiktok?.audience;
  return (
    audienceHasGenderOrAge(ytAudience) ||
    audienceHasGenderOrAge(igAudience) ||
    audienceHasGenderOrAge(ttAudience)
  );
}

/** Follower counts aligned with podscopeExample media kit demo. */
export const DEMO_AUDIENCE_STATISTICS: PodcastMediaKit["statistics"] = {
  episodeCount: 0,
  totalViews: 0,
  totalLikes: 0,
  totalComments: 0,
  youtubeSubscribers: 95_000,
  spotifyFollowers: 0,
  instagramFollowers: 28_500,
  tiktokFollowers: 62_000,
};

const ytAudience: YoutubeAudienceAnalytics = {
  gender: [
    { type: "male", confidence: 48 },
    { type: "female", confidence: 47 },
    { type: "unknown", confidence: 5 },
  ],
  ageRanges: [
    { range: "13-17", confidence: 12 },
    { range: "18-24", confidence: 38 },
    { range: "25-34", confidence: 28 },
    { range: "35-44", confidence: 14 },
    { range: "45-54", confidence: 6 },
    { range: "55-64", confidence: 2 },
  ],
  countries: [
    { code: "CL", views: 42_000, estimatedMinutesWatched: 18_000, sharePercent: 42 },
    { code: "AR", views: 18_000, estimatedMinutesWatched: 7_500, sharePercent: 18 },
    { code: "MX", views: 12_000, estimatedMinutesWatched: 5_000, sharePercent: 12 },
    { code: "CO", views: 8_000, estimatedMinutesWatched: 3_200, sharePercent: 8 },
    { code: "PE", views: 6_000, estimatedMinutesWatched: 2_400, sharePercent: 6 },
    { code: "ES", views: 5_000, estimatedMinutesWatched: 2_100, sharePercent: 5 },
    { code: "US", views: 4_000, estimatedMinutesWatched: 1_600, sharePercent: 4 },
    { code: "VE", views: 3_000, estimatedMinutesWatched: 1_200, sharePercent: 3 },
    { code: "EC", views: 2_000, estimatedMinutesWatched: 800, sharePercent: 2 },
  ],
  cities: [
    { name: "Santiago", views: 22_000, estimatedMinutesWatched: 9_500, sharePercent: 22 },
    { name: "Buenos Aires", views: 14_000, estimatedMinutesWatched: 5_800, sharePercent: 14 },
    { name: "Ciudad de México", views: 10_000, estimatedMinutesWatched: 4_200, sharePercent: 10 },
    { name: "Lima", views: 7_000, estimatedMinutesWatched: 2_900, sharePercent: 7 },
    { name: "Bogotá", views: 6_000, estimatedMinutesWatched: 2_500, sharePercent: 6 },
    { name: "Valparaíso", views: 5_000, estimatedMinutesWatched: 2_100, sharePercent: 5 },
    { name: "Medellín", views: 4_000, estimatedMinutesWatched: 1_600, sharePercent: 4 },
    { name: "Montevideo", views: 3_000, estimatedMinutesWatched: 1_200, sharePercent: 3 },
    { name: "Quito", views: 2_500, estimatedMinutesWatched: 1_000, sharePercent: 2.5 },
  ],
};

/** Analytics slice for buildDemographicsTable (gender/age only). */
export const DEMO_AUDIENCE_ANALYTICS: NonNullable<
  PodcastMediaKit["analytics"]
> = {
  youtube: {
    longForm: { audience: ytAudience },
  },
  instagram: {
    audience: {
      gender: [
        { type: "MALE", percentage: 48 },
        { type: "FEMALE", percentage: 50 },
        { type: "UNDEFINED", percentage: 2 },
      ],
      ageRanges: [
        { range: "18-24", percentage: 22 },
        { range: "25-34", percentage: 36 },
        { range: "35-44", percentage: 26 },
        { range: "45-54", percentage: 12 },
        { range: "55+", percentage: 4 },
      ],
    },
  },
  tiktok: {
    audience: {
      selfReported: true,
      gender: [
        { type: "Male", percentage: 44 },
        { type: "Female", percentage: 54 },
        { type: "Other", percentage: 2 },
      ],
      ageRanges: [
        { range: "18-24", percentage: 35 },
        { range: "25-34", percentage: 40 },
        { range: "35-44", percentage: 18 },
        { range: "45+", percentage: 7 },
      ],
    },
  },
};

export function buildDemoDemographicsTable() {
  return buildDemographicsTable(
    DEMO_AUDIENCE_ANALYTICS,
    DEMO_AUDIENCE_STATISTICS,
  );
}

export function getDemoYoutubeGeoAudience(): NonNullable<
  YoutubeAudienceAnalytics
> {
  return {
    countries: ytAudience.countries,
    cities: ytAudience.cities,
  };
}

export const AUDIENCE_DEMO_DISCLAIMER_ES =
  "Datos de ejemplo — conecta tus cuentas para ver métricas reales de audiencia.";

/** Merges demo audience analytics + follower counts into a PDF export payload. */
export function applyAudienceDemoToExportPayload(
  payload: StatsPayload,
): StatsPayload {
  const analytics = payload.analytics ?? {};
  const demoYtAudience =
    DEMO_AUDIENCE_ANALYTICS.youtube?.longForm?.audience ??
    DEMO_AUDIENCE_ANALYTICS.youtube?.audience;

  const demoGeo = getDemoYoutubeGeoAudience();

  return {
    ...payload,
    statistics: {
      ...payload.statistics,
      youtubeSubscribers: DEMO_AUDIENCE_STATISTICS.youtubeSubscribers,
      instagramFollowers: DEMO_AUDIENCE_STATISTICS.instagramFollowers,
      tiktokFollowers: DEMO_AUDIENCE_STATISTICS.tiktokFollowers,
    },
    analytics: {
      ...analytics,
      youtube: {
        ...analytics.youtube,
        longForm: {
          ...analytics.youtube?.longForm,
          audience: {
            ...demoYtAudience,
            countries: demoGeo.countries,
            cities: demoGeo.cities,
          },
        },
      },
      instagram: {
        ...analytics.instagram,
        audience: DEMO_AUDIENCE_ANALYTICS.instagram?.audience,
      },
      tiktok: {
        ...analytics.tiktok,
        audience: DEMO_AUDIENCE_ANALYTICS.tiktok?.audience,
      },
    },
  };
}
